import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input } from "antd";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { merge } from "lodash";

import Modals from "../../common/modal";
import {
  asyncCreateMeetingPoint,
  asyncEditMeetingPoint,
  asyncGetMeetingPoint,
} from "../../../redux/slices/meetingPointSlice";
import { ltrim } from "../../../util/helper";
import { MeetingPointFormSchema } from "../../../validators/formSchema";

const MeetingPointModal = ({
  openModal,
  setOpenModal,
  messageApi,
  modalData,
}) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { loading, pagination, editLoading } = useSelector((state) => ({
    loading: state.meetingPoint.createMeetingPoint.loading,
    pagination: state.meetingPoint.getMeetingPoint.pagination,
    editLoading: state.meetingPoint.editMeetingPoint.loading,
  }));

  const [isDirty, setIsDirty] = useState(false);
  const [prevLat, setPrevLat] = useState("");
  const [prevLong, setPrevLong] = useState("");
  const [isLatLongChanged, setIsLatLongChanged] = useState(false);

  useEffect(() => {
    if (modalData?.data) {
      setPrevLat(modalData.data?.latitude);
      setPrevLong(modalData.data?.longitude);
    }
  }, [modalData]);

  const handleLatLongChange = (values) => {
    const isLatChanged = values.latitude !== prevLat;
    const isLongChanged = values.longitude !== prevLong;
    setIsLatLongChanged(isLatChanged || isLongChanged);
  };

  const footerElem = [
    <div className="modal_footer">
      <Button
        key="submit"
        loading={modalData.mode === "add" ? loading : editLoading}
        disabled={modalData.mode === "edit" && !isDirty}
        onClick={() => {
          if (formRef.current) {
            formRef.current.handleSubmit();
          }
        }}
      >
        {modalData.mode === "add" ? "Submit" : "Update"}
      </Button>
      <Button key="back" onClick={() => setOpenModal(false)}>
        Cancel
      </Button>
    </div>,
  ];

  const initialValue = {
    name: "",
    latitude: "",
    longitude: "",
  };

  return (
    <Modals
      title={
        modalData.mode === "edit"
          ? "Update Meeting Point"
          : "Create New Meeting Point"
      }
      open={openModal}
      setOpen={setOpenModal}
      footer={footerElem}
      width={700}
    >
      <Formik
        innerRef={formRef}
        validationSchema={MeetingPointFormSchema}
        initialValues={
          modalData?.mode
            ? merge(initialValue, { ...modalData.data })
            : initialValue
        }
        onSubmit={(values) => {
          const trimValue = {
            name: values.name?.trimEnd() || "",
            latitude: values.latitude?.trimEnd() || "",
            longitude: values.longitude?.trimEnd() || "",
          };
          const newValues = {
            ...trimValue,
            id: values.id,
            isLatLongChanged: isLatLongChanged,
          };

          if (modalData.mode === "add") {
            dispatch(asyncCreateMeetingPoint(trimValue)).then((res) => {
              if (res.payload?.status === 201) {
                messageApi.open({
                  type: "success",
                  content:
                    res.payload?.data?.message ||
                    "Terminal Created Successfully",
                });
                dispatch(
                  asyncGetMeetingPoint({
                    page: pagination.page,
                    limit: pagination.pageSize,
                  })
                );
                setOpenModal(false);
              } else {
                messageApi.open({
                  type: "error",
                  content: res.payload?.message || "Something went wrong",
                });
              }
            });
          } else {
            dispatch(asyncEditMeetingPoint(newValues)).then((res) => {
              if (res.payload?.status === 200) {
                messageApi.open({
                  type: "success",
                  content:
                    res.payload?.data?.message ||
                    "Terminal Updated Successfully",
                });
                dispatch(
                  asyncGetMeetingPoint({
                    page: pagination.page,
                    limit: pagination.pageSize,
                  })
                );
                setOpenModal(false);
              } else {
                messageApi.open({
                  type: "error",
                  content: res.payload?.message || "Something went wrong",
                });
              }
            });
          }
        }}
      >
        {({
          handleChange,
          handleSubmit,
          values,
          errors,
          touched,
          setDirty,
        }) => (
          <div className="custom-scrollbar form_div">
            {/* <Divider style={{ margin: "10px 0" }} /> */}
            <Form
              onFinish={(e) => {
                setDirty(false);
                handleSubmit(e);
              }}
            >
              <Form.Item style={{ margin: "0" }}>
                <label htmlFor="name1" className="form-label required">
                  Name
                </label>
                <Input
                  id="name1"
                  type="text"
                  name="name"
                  className="form-input"
                  placeholder="Enter name"
                  onChange={(e) => {
                    if (handleChange) {
                      handleChange(e);
                      setIsDirty(true);
                    }
                  }}
                  value={values?.name ? ltrim(values?.name) : ""}
                  status={`${(errors.name && touched.name && "error") || ""}`}
                />
              </Form.Item>
              {errors.name && touched.name && (
                <small id="name-help" className="error-message">
                  {errors.name}
                </small>
              )}
              <div className="form_flex_two">
                <div className="form_flex_two_left">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="latitude1" className="form-label required">
                      Latitude
                    </label>
                    <Input
                      id="latitude1"
                      type="text"
                      name="latitude"
                      className="form-input"
                      placeholder="Enter latitude"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                          handleLatLongChange({
                            ...values,
                            latitude: e.target.value,
                          });
                        }
                      }}
                      value={values?.latitude ? ltrim(values?.latitude) : ""}
                      status={`${
                        (errors.latitude && touched.latitude && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.latitude && touched.latitude && (
                    <small id="latitude-help" className="error-message">
                      {errors.latitude}
                    </small>
                  )}
                </div>
                <div className="form_flex_two_right">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="longitude1" className="form-label required">
                      Longitude
                    </label>
                    <Input
                      id="longitude1"
                      type="text"
                      name="longitude"
                      className="form-input"
                      placeholder="Enter longitude"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                          handleLatLongChange({
                            ...values,
                            longitude: e.target.value,
                          });
                        }
                      }}
                      value={values?.longitude ? ltrim(values?.longitude) : ""}
                      status={`${
                        (errors.longitude && touched.longitude && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.longitude && touched.longitude && (
                    <small id="longitude-help" className="error-message">
                      {errors.longitude}
                    </small>
                  )}
                </div>
              </div>
            </Form>
          </div>
        )}
      </Formik>
    </Modals>
  );
};

export default MeetingPointModal;
